import { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useOptimiserRuns, useRunOutputFull, useOptimiserInputTable } from './Optimiser.data'
import { TableSection } from './Optimiser.sections'
import { ProductionLineChart } from './ProductionLineChart'
import { InventoryChart } from './InventoryChart'
import { UnmetDemandChart } from './UnmetDemandChart'
import { TOKENS, FONT_SANS, FONT_MONO } from './OptimiserDesign'

type Row = Record<string, unknown>
const getNum = (r: Row, k: string) => Number(r[k] ?? 0)
const fmtN = (n: number) => n.toLocaleString('en-GB', { maximumFractionDigits: 0 })
const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`

// ── KPI summary panel ────────────────────────────────────────────────────────

const KpiTile = ({
  label, value, accent, warn,
}: {
  label: string; value: string; accent?: boolean; warn?: boolean
}) => (
  <Box sx={{
    background: TOKENS.surface50, border: `1px solid ${TOKENS.line200}`,
    borderRadius: '6px', px: 1.5, py: 1.25,
  }}>
    <Typography sx={{
      fontFamily: FONT_MONO, fontSize: 22, fontWeight: 700, lineHeight: 1.1,
      color: warn ? '#C00000' : accent ? TOKENS.brandTeal : TOKENS.ink900,
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontFamily: FONT_SANS, fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.05em', color: TOKENS.ink300, mt: 0.5,
    }}>
      {label}
    </Typography>
  </Box>
)

const KpiPanel = ({
  prodRows, capRows, invRows, unmetRows, demandRows,
}: {
  prodRows: Row[]; capRows: Row[]; invRows: Row[]; unmetRows: Row[]; demandRows: Row[]
}) => {
  const totalVolume = prodRows.reduce((s, r) => s + getNum(r, 'value'), 0)
  const totalUnmet  = unmetRows.reduce((s, r) => s + getNum(r, 'value'), 0)
  const totalDemand = demandRows.reduce((s, r) => s + getNum(r, 'value'), 0)
  const fillRate    = totalDemand > 0 ? 1 - totalUnmet / totalDemand : 1

  const closingVals = invRows.map((r) => getNum(r, 'closeInventory'))
  const avgInv = closingVals.length
    ? closingVals.reduce((a, b) => a + b, 0) / closingVals.length : 0

  const unmetByPeriod: Record<number, number> = {}
  unmetRows.forEach((r) => {
    const t = getNum(r, 'period')
    unmetByPeriod[t] = (unmetByPeriod[t] ?? 0) + getNum(r, 'value')
  })
  const weeksImpacted = Object.values(unmetByPeriod).filter((v) => v > 0).length

  let avgUtil: number | null = null
  if (capRows.length > 0) {
    const ratios = capRows.map((r) => {
      const c = getNum(r, 'cap')
      return c ? getNum(r, 'load') / c : 0
    })
    avgUtil = ratios.reduce((a, b) => a + b, 0) / ratios.length
  }

  const tiles = [
    { label: 'Fill Rate',       value: fmtPct(fillRate),  accent: fillRate >= 0.95, warn: fillRate < 0.9 },
    { label: 'Total Unmet',     value: fmtN(totalUnmet),  warn: totalUnmet > 0 },
    { label: 'Total Volume',    value: fmtN(totalVolume), accent: true },
    { label: 'Avg Inventory',   value: fmtN(avgInv) },
    { label: 'Weeks Impacted',  value: String(weeksImpacted), warn: weeksImpacted > 0 },
    ...(avgUtil !== null
      ? [{ label: 'Avg Line Util', value: fmtPct(avgUtil) }]
      : []),
  ]

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <Typography sx={{ fontFamily: FONT_SANS, fontSize: 12, fontWeight: 700, color: TOKENS.ink900, mb: 1.5 }}>
        Summary KPIs
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, alignContent: 'start' }}>
        {tiles.map((t) => (
          <KpiTile key={t.label} label={t.label} value={t.value} accent={t.accent} warn={t.warn} />
        ))}
      </Box>
    </Box>
  )
}

// ── Grid cell wrapper ────────────────────────────────────────────────────────

const GridCell = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
    border: `1px solid ${TOKENS.line200}`, borderRadius: '8px', background: TOKENS.surface0,
  }}>
    {children}
  </Box>
)

// ── Run selector strip ───────────────────────────────────────────────────────

const RunSelector = ({
  runs, selectedRunId, onSelect,
}: {
  runs: { run_id: string; phase: number; status: string; objective: number | null }[]
  selectedRunId: string | null
  onSelect: (id: string) => void
}) => (
  <Box sx={{
    px: 2, py: 1, borderBottom: `1px solid ${TOKENS.line200}`, display: 'flex',
    alignItems: 'center', gap: 1.5, overflowX: 'auto', flexShrink: 0,
  }}>
    <Typography sx={{ fontFamily: FONT_SANS, fontSize: 10.5, fontWeight: 700, color: TOKENS.ink400,
                      textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
      Run
    </Typography>
    {runs.map((r) => (
      <Box
        key={r.run_id}
        onClick={() => onSelect(r.run_id)}
        sx={{
          px: '10px', py: '4px', borderRadius: '100px', flexShrink: 0, cursor: 'pointer',
          border: `1.5px solid ${selectedRunId === r.run_id ? TOKENS.brandTeal : TOKENS.line300}`,
          background: selectedRunId === r.run_id ? TOKENS.blue50 : 'transparent',
          color: selectedRunId === r.run_id ? TOKENS.brandTeal : TOKENS.ink700,
          '&:hover': { borderColor: TOKENS.brandTeal },
        }}
      >
        <Typography component="span" sx={{ fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600 }}>
          {r.run_id}
        </Typography>
        <Typography component="span" sx={{ fontFamily: FONT_SANS, fontSize: 11, color: 'inherit', ml: 0.75 }}>
          · Ph{r.phase}
        </Typography>
      </Box>
    ))}
  </Box>
)

// ── Main panel ───────────────────────────────────────────────────────────────

export const ChartPanel = ({
  scenarioId,
}: {
  section: TableSection
  scenarioId?: string
}) => {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const { data: runsData, isLoading: runsLoading } = useOptimiserRuns(scenarioId)
  const runs = runsData?.runs ?? []

  // Auto-select most recent successful run
  useEffect(() => {
    if (!selectedRunId && runs.length > 0) {
      const best = runs.find((r) => r.status === 'ok') ?? runs[0]
      if (best) setSelectedRunId(best.run_id)
    }
  }, [runs, selectedRunId])

  useEffect(() => { setSelectedRunId(null) }, [scenarioId])

  const prodQ   = useRunOutputFull(selectedRunId ?? '', 'production',     scenarioId)
  const capQ    = useRunOutputFull(selectedRunId ?? '', 'capacity_util',  scenarioId)
  const invQ    = useRunOutputFull(selectedRunId ?? '', 'inventory_flow', scenarioId)
  const unmetQ  = useRunOutputFull(selectedRunId ?? '', 'lost_sales',     scenarioId)
  const demandQ = useOptimiserInputTable('demand', scenarioId)

  const prodRows:   Row[] = prodQ.data?.data   ?? []
  const capRows:    Row[] = capQ.data?.data    ?? []
  const invRows:    Row[] = invQ.data?.data    ?? []
  const unmetRows:  Row[] = unmetQ.data?.data  ?? []
  const demandRows: Row[] = demandQ.data?.data ?? []

  const coreLoading = !!selectedRunId && (prodQ.isLoading || invQ.isLoading || unmetQ.isLoading)

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {/* Run selector */}
      {runsLoading ? (
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${TOKENS.line200}`, flexShrink: 0 }}>
          <CircularProgress size={16} />
        </Box>
      ) : runs.length === 0 ? (
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${TOKENS.line200}`, flexShrink: 0 }}>
          <Typography sx={{ fontFamily: FONT_SANS, fontSize: 12, color: TOKENS.ink400 }}>
            No runs yet — use Run Solver to generate outputs.
          </Typography>
        </Box>
      ) : (
        <RunSelector runs={runs} selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
      )}

      {/* 2×2 grid */}
      {!selectedRunId ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontFamily: FONT_SANS, fontSize: 13, color: TOKENS.ink300 }}>
            Select a run above
          </Typography>
        </Box>
      ) : coreLoading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{
          flex: 1, minHeight: 0, display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: '8px', p: '8px',
        }}>
          {/* TL — KPIs */}
          <GridCell>
            <KpiPanel
              prodRows={prodRows} capRows={capRows}
              invRows={invRows} unmetRows={unmetRows} demandRows={demandRows}
            />
          </GridCell>

          {/* TR — Production */}
          <GridCell>
            <ProductionLineChart prodRows={prodRows} capRows={capRows} />
          </GridCell>

          {/* BL — Inventory */}
          <GridCell>
            <InventoryChart rows={invRows} />
          </GridCell>

          {/* BR — Unmet Demand */}
          <GridCell>
            <UnmetDemandChart unmetRows={unmetRows} demandRows={demandRows} />
          </GridCell>
        </Box>
      )}
    </Box>
  )
}
